/**
 * charts/EfficiencyCharts.tsx — visuals for the register findings
 * ================================================================
 * Four charts, kept in one module because they are only ever used together on
 * the Efficiency page. Each takes a series produced by src/lib/features/
 * registerFindings.ts, so the numbers here and the numbers in the weekly report
 * come from the same arithmetic and cannot drift.
 *
 * Phase colours follow the electrical convention rather than a chart palette —
 * R red, Y yellow, B blue — because an electrician reading this already knows
 * that code and any other choice would fight it.
 */

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  LeadingPfPoint,
  ThdPoint,
} from '@/lib/features/registerFindings';
import { useLanguage } from '@/hooks/useLanguage';

// ─── Shared styling ───────────────────────────────────────────────────────────

const AXIS = { fontSize: 11, fontWeight: 700 as const, fill: '#64748B' };
const GRID = '#E2E8F0';

const C = {
  floor:     '#0E7490',
  peak:      '#CBD5E1',
  offHours:  '#4F46E5',
  cumulative:'#0F766E',
  phaseR:    '#DC2626',
  phaseY:    '#CA8A04',
  phaseB:    '#2563EB',
  limit:     '#DC2626',
  working:   '#059669',
  idle:      '#F59E0B',
  off:       '#CBD5E1',
};

const TOOLTIP_STYLE = {
  contentStyle: {
    borderRadius: 8,
    border: '1px solid #E2E8F0',
    fontSize: 12,
    fontWeight: 600,
  },
} as const;

/** "2026-08-03" → "3 Aug", so a 30-day axis stays readable. */
function shortDay(day: string): string {
  const [, m, d] = day.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${Number(d)} ${months[Number(m) - 1] ?? ''}`.trim();
}

const round1 = (n: number) => Math.round(n * 10) / 10;

// ─── Feature 22 — leading reactive energy ────────────────────────────────────

export function LeadingPfChart({ data, height = 240 }: { data: LeadingPfPoint[]; height?: number }) {
  const { t } = useLanguage();
  const rows = data.map(p => ({
    label: shortDay(p.day),
    cap: round1(p.capKvarh),
    cumulative: round1(p.cumulativeKvarh),
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows} margin={{ top: 22, right: 8, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={18} />
        <YAxis yAxisId="left" tick={AXIS} tickLine={false} axisLine={false} width={44}
               label={{ value: 'kVArh', position: 'top', offset: 10, fontSize: 10, fontWeight: 700, fill: '#94A3B8' }} />
        <YAxis yAxisId="right" orientation="right" tick={AXIS} tickLine={false} axisLine={false} width={44} />
        <Tooltip {...TOOLTIP_STYLE} formatter={(v: number, n: string) => [`${v} kVArh`, t(n)]} />
        <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} />
        <Bar yAxisId="left" dataKey="cap" name={t('Leading energy')}
             fill={C.offHours} radius={[3, 3, 0, 0]} />
        <Line yAxisId="right" type="monotone" dataKey="cumulative" name={t('Running total')}
              stroke={C.cumulative} strokeWidth={2} dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ─── Feature 8 — voltage THD against the IEEE 519 limit ──────────────────────

export function VoltageThdChart({ data, height = 240 }: { data: ThdPoint[]; height?: number }) {
  const { t } = useLanguage();
  const limit = data[0]?.limit ?? 8;
  const rows = data.map(p => ({
    label: shortDay(p.day),
    R: round1(p.vR), Y: round1(p.vY), B: round1(p.vB),
  }));
  const ceiling = Math.max(limit * 1.25, ...rows.flatMap(r => [r.R, r.Y, r.B]));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={rows} margin={{ top: 22, right: 8, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={18} />
        <YAxis tick={AXIS} tickLine={false} axisLine={false} width={44} domain={[0, Math.ceil(ceiling)]}
               label={{ value: '%', position: 'top', offset: 10, fontSize: 10, fontWeight: 700, fill: '#94A3B8' }} />
        <Tooltip {...TOOLTIP_STYLE} formatter={(v: number, n: string) => [`${v}%`, `${t('Phase')} ${n}`]} />
        <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700 }} />
        <ReferenceLine y={limit} stroke={C.limit} strokeDasharray="5 4" strokeWidth={1.5}
                       label={{ value: `IEEE 519 — ${limit}%`, position: 'insideTopRight',
                                fontSize: 10, fontWeight: 700, fill: C.limit }} />
        <Line type="monotone" dataKey="R" stroke={C.phaseR} strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="Y" stroke={C.phaseY} strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="B" stroke={C.phaseB} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}
